import { AppNativeSDK } from '@/modules/native-app'
import {
    AdProviderTypes,
    HeaderBiddingAdSettings,
    PlatformType,
    type AdProvidersSettings,
    type AdSenseProviderSettings,
    type AppProviderSettings,
    type EffectiveType,
    type GameDistributionsSettings,
    type GoogleAdBannerSettings,
    type GoogleAdSettings,
    type GoogleInstreamSettings,
} from '@/types'

import {
    INDEX_EXCHANGE,
    MEDIA_NET_BANNER,
    MEDIA_NET_MULTIFORMAT,
    MEDIA_NET_VIDEO,
    NEXVERSE,
    ONETAG,
    OMNIDEX_BANNER,
    OMNIDEX_VIDEO,
    PLAYGAMA_FALLBACK,
    PLAYGAMA,
    TAPPX_BANNER,
    TAPPX_VIDEO,
    LOOPME,
    STARTIO,
    ADITUDE_DISPLAY,
    ADITUDE_VIDEO,
    EQUATIV,
    INMOBI_BANNER,
    INMOBI_VIDEO,
} from './header-bidding/bidders-config'
import { AdType } from './types'

// Factory Types - require adUnitConfig, make rest optional

type GoogleAdOverrides = Pick<GoogleAdSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleAdSettings['options'], 'adUnitConfig'>>

type GoogleBannerOverrides = Pick<GoogleAdBannerSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleAdBannerSettings['options'], 'adUnitConfig'>>

type GoogleInstreamOverrides = Pick<GoogleInstreamSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleInstreamSettings['options'], 'adUnitConfig' | 'name'>>

type AppNativeOverrides = Pick<AppProviderSettings['options'], 'adUnitConfig'> &
    Partial<Omit<AppProviderSettings['options'], 'adUnitConfig'>>

type AdSenseOverrides = Pick<AdSenseProviderSettings['options'], 'adUnitConfig'> &
    Partial<Omit<AdSenseProviderSettings['options'], 'adUnitConfig'>>

// Constants

const DEFAULT_AD_UNIT_TIMEOUT = 3000
const APP_NATIVE_TIMEOUT = 6000
const INSTREAM_TIMEOUT = 4000

const GOOGLE_AD_PAGE_DOMAIN = 'playgama.com'
const GOOGLE_AD_PAGE_URL = 'https://playgama.com'

const VERY_SLOW_EFFECTIVE_TYPES = ['2g', 'slow-2g']
const SLOW_EFFECTIVE_TYPES = [...VERY_SLOW_EFFECTIVE_TYPES, '3g']

const STATIC_GOOGLE_AD_UNITS: Record<string, string> = {
    sticky_mobile_non_game_page: 'sticky_mobile',
    sticky_mobile: 'sticky_mobile',
    sticky_tablet: 'sticky_mobile',
    sticky_desktop: 'sticky_desktop',
    sidebar: 'sidebar',
    sidebar_bottom: 'sidebar_bottom',
    sidebar_fullscreen: 'sidebar_fullscreen',
    sidebar_bottom_fullscreen: 'sidebar_bottom_fullscreen',
    leaderboard: 'leaderboard',
    leaderboard_rest: 'leaderboard_rest',
    widget_sidebar: 'widget_sidebar',
    widget_sidebar_bottom: 'widget_sidebar_bottom',
    widget_horizontal: 'widget_horizontal',
    widget_horizontal_2: 'widget_horizontal_2',
    widget_sticky_mobile: 'widget_sticky_mobile',
    widget_sticky_tablet: 'widget_sticky_mobile',
    interstitial_on_return: 'interstitial_on_return',
    universal_banner_1: 'universal_banner_1',
    universal_banner_short_1: 'universal_banner_1',
    universal_banner_2: 'universal_banner_2',
}

const AD_SENSE_PORTAL_CLIENT_ID = 'ca-pub-2864899775153797'
const STATIC_GOOGLE_AD_SENSE_UNITS: Record<string, string> = {
    sticky_mobile: '2256255959',
    sticky_desktop: '2256255959',
}

// Native (AppLovin) ad units — used once the native SDK ships (Android ≥ 1.1.09 / iOS ≥ 1.1.3).
const ANDROID_APPLOVIN_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: 'adc0a523dde2c2f1',
    rewarded: 'd0ef11af6f54f0fb',
}
const IOS_APPLOVIN_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: 'da96af9802e81f8f',
    rewarded: '23193cc48219498c',
}

// GAM fallback ad units for older app versions without the native SDK.
const ANDROID_GAM_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: '/23211928466/interstitial_android',
    interstitial_preroll: '/23211928466/interstitial_preroll_android',
    rewarded: '/23211928466/rewarded_android',
}
const IOS_GAM_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: '/23211928466/interstitial_ios',
    interstitial_preroll: '/23211928466/interstitial_preroll_ios',
    rewarded: '/23211928466/rewarded_ios',
}
const TEST_GAM_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: '/21775744923/example/interstitial',
    interstitial_preroll: '/21775744923/example/interstitial',
    rewarded: '/21775744923/example/rewarded',
}

export function getAdProvidersSettings({
    gamAccount,
    appNativeSDK,
    platform,
    effectiveType,
    isVersionAtLeast,
    gameDistributionGameId,
}: {
    gamAccount: string
    appNativeSDK: AppNativeSDK | undefined
    platform: PlatformType
    effectiveType: EffectiveType | undefined
    isVersionAtLeast: (minVersions: { ios?: string; android?: string }) => boolean
    gameDistributionGameId: string | undefined
}) {
    const hasAndroidApplovinSdk = isVersionAtLeast({ android: '1.1.09' })
    const hasIosApplovinSdk = isVersionAtLeast({ ios: '1.1.3' })

    const androidNativeAdUnits = hasAndroidApplovinSdk ? ANDROID_APPLOVIN_AD_UNITS : ANDROID_GAM_AD_UNITS
    const iosNativeAdUnits = hasIosApplovinSdk ? IOS_APPLOVIN_AD_UNITS : IOS_GAM_AD_UNITS
    const androidTestNativeAdUnits = hasAndroidApplovinSdk ? ANDROID_APPLOVIN_AD_UNITS : TEST_GAM_AD_UNITS
    const iosTestNativeAdUnits = hasIosApplovinSdk ? IOS_APPLOVIN_AD_UNITS : TEST_GAM_AD_UNITS

    const isSlowConnection = effectiveType && SLOW_EFFECTIVE_TYPES.includes(effectiveType)
    const isVerySlowConnection = effectiveType && VERY_SLOW_EFFECTIVE_TYPES.includes(effectiveType)

    const createGoogleAdSettings = (overrides: GoogleAdOverrides): GoogleAdSettings => ({
        type: AdProviderTypes.GAM_H5,
        options: {
            platform,
            pageDomain: GOOGLE_AD_PAGE_DOMAIN,
            gamAccount,
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            ...overrides,
        },
    })

    const createGoogleBannerSettings = (overrides: GoogleBannerOverrides): GoogleAdBannerSettings => ({
        type: AdProviderTypes.GAM_BANNER_INTERSTITIAL,
        options: {
            platform,
            pageDomain: GOOGLE_AD_PAGE_DOMAIN,
            gamAccount,
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            ...overrides,
        },
    })

    const createGoogleInstreamSettings = (overrides: GoogleInstreamOverrides): GoogleInstreamSettings => ({
        type: AdProviderTypes.GAM_INSTREAM,
        options: {
            headerBidding: {
                bidders: [PLAYGAMA],
            },
            platform,
            pageURL: GOOGLE_AD_PAGE_URL,
            gamAccount,
            timeout: INSTREAM_TIMEOUT,
            useCurrentPageUrl: true,
            ...overrides,
        },
    })

    const createAppNativeSettings = (overrides: AppNativeOverrides): AppProviderSettings => ({
        type: AdProviderTypes.APP,
        options: {
            appNativeSDK,
            timeout: APP_NATIVE_TIMEOUT,
            platform,
            ...overrides,
        },
    })

    const createHeaderBiddingAdSettings = (
        bidders: HeaderBiddingAdSettings['options']['bidders'],
    ): HeaderBiddingAdSettings => ({
        type: AdProviderTypes.PREBID_DIRECT,
        options: {
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            platform,
            bidders,
        },
    })

    const createAdSenseSettings = (overrides: AdSenseOverrides): AdSenseProviderSettings => ({
        type: AdProviderTypes.GOOGLE_AD_SENSE,
        options: {
            platform,
            clientId: AD_SENSE_PORTAL_CLIENT_ID,
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            ...overrides,
        },
    })

    const GOOGLE_AD_PORTAL_SETTINGS = createGoogleAdSettings({
        adUnitConfig: {
            desktop: STATIC_GOOGLE_AD_UNITS,
            mobile: STATIC_GOOGLE_AD_UNITS,
        },
        headerBidding: {
            bidders: [
                ONETAG,
                TAPPX_BANNER,
                MEDIA_NET_BANNER,
                PLAYGAMA,
                INMOBI_BANNER,
                OMNIDEX_BANNER,
                LOOPME,
                ADITUDE_DISPLAY,
                EQUATIV,
                INDEX_EXCHANGE,
            ],
        },
    })

    const GAME_PAGE_H5_HEADER_BIDDING_BIDDERS = [
        PLAYGAMA,
        ADITUDE_DISPLAY,
        EQUATIV,
        INDEX_EXCHANGE,
        INMOBI_BANNER,
        OMNIDEX_BANNER,
        MEDIA_NET_BANNER,
    ]

    const GOOGLE_AD_GAME_PAGE_H5_SETTINGS = createGoogleAdSettings({
        adUnitConfig: {
            desktop: {
                ...STATIC_GOOGLE_AD_UNITS,
                interstitial: 'h5_interstitial_desktop',
                interstitial_preroll: 'h5_interstitial_preroll_desktop',
                rewarded: 'rewarded_desktop',
            },
            mobile: {
                ...STATIC_GOOGLE_AD_UNITS,
                interstitial: 'h5_interstitial_mobile',
                interstitial_preroll: 'h5_interstitial_preroll_mobile',
                rewarded: 'rewarded_mobile',
            },
        },
        headerBidding: { bidders: GAME_PAGE_H5_HEADER_BIDDING_BIDDERS },
        noPrebidPlacements: ['interstitial_preroll', 'rewarded'],
    })

    const GOOGLE_AD_GAME_PAGE_H5_INTER_SETTINGS = createGoogleAdSettings({
        adUnitConfig: {
            desktop: {
                rewarded: 'rewarded_2_desktop',
                interstitial_preroll: 'h5_interstitial_preroll_2_desktop',
            },
            mobile: {
                rewarded: 'rewarded_2_mobile',
                interstitial_preroll: 'h5_interstitial_preroll_2_mobile',
            },
        },
        headerBidding: { bidders: GAME_PAGE_H5_HEADER_BIDDING_BIDDERS },
    })

    const GOOGLE_AD_WIDGET_SETTINGS = createGoogleAdSettings({
        adUnitConfig: {
            desktop: {
                rewarded: 'widget_rewarded_desktop',
                interstitial: 'widget_h5_interstitial_desktop',
                interstitial_preroll: 'widget_h5_interstitial_preroll_desktop',
            },
            mobile: {
                rewarded: 'widget_rewarded_mobile',
                interstitial: 'widget_h5_interstitial_mobile',
                interstitial_preroll: 'widget_h5_interstitial_preroll_mobile',
            },
        },
    })

    const GOOGLE_BANNER_GAME_PAGE_SETTINGS = createGoogleBannerSettings({
        timeout: DEFAULT_AD_UNIT_TIMEOUT,
        headerBidding: {
            bidders: [
                NEXVERSE,
                MEDIA_NET_BANNER,
                PLAYGAMA,
                INMOBI_BANNER,
                OMNIDEX_BANNER,
                LOOPME,
                ADITUDE_DISPLAY,
                EQUATIV,
                INDEX_EXCHANGE,
                ONETAG,
                TAPPX_BANNER,
            ],
        },
        adUnitConfig: {
            desktop: {
                interstitial_preroll: 'banner_ingame_preroll_desktop',
                interstitial: 'banner_ingame_desktop',
                rewarded: 'banner_ingame_rewarded_desktop',
            },
            mobile: {
                interstitial_preroll: 'banner_ingame_preroll_mobile',
                interstitial: 'banner_ingame_mobile',
                rewarded: 'banner_ingame_rewarded_mobile',
            },
        },
    })

    const GOOGLE_INSTREAM_GAME_PAGE_SETTINGS = createGoogleInstreamSettings({
        adUnitConfig: {
            desktop: {
                rewarded: 'instream_rewarded_desktop',
                interstitial: 'instream_desktop',
                interstitial_preroll: 'instream_preroll_desktop',
            },
            mobile: {
                rewarded: 'instream_rewarded_mobile',
                interstitial: 'instream_mobile',
                interstitial_preroll: 'instream_preroll_mobile',
            },
        },
        headerBidding: {
            bidders: [
                MEDIA_NET_VIDEO,
                PLAYGAMA,
                INMOBI_VIDEO,
                OMNIDEX_VIDEO,
                LOOPME,
                ADITUDE_VIDEO,
                EQUATIV,
                INDEX_EXCHANGE,
                TAPPX_VIDEO,
                ONETAG,
            ],
        },
    })

    const GOOGLE_INSTREAM_CHEAP_MINIPLAY_SETTINGS = createGoogleInstreamSettings({
        adUnitConfig: {
            desktop: {
                interstitial_preroll: 'instream_preroll_no_floor_desktop',
                interstitial: 'instream_no_floor_desktop',
                rewarded: 'instream_rewarded_no_floor_desktop',
            },
            mobile: {
                interstitial_preroll: 'instream_preroll_no_floor_mobile',
                interstitial: 'instream_no_floor_mobile',
                rewarded: 'instream_rewarded_no_floor_mobile',
            },
        },
    })

    const GOOGLE_INSTREAM_WIDGET_SETTINGS = createGoogleInstreamSettings({
        timeout: DEFAULT_AD_UNIT_TIMEOUT,
        adUnitConfig: {
            desktop: {
                rewarded: 'widget_instream_rewarded_desktop',
                interstitial: 'widget_instream_desktop',
                interstitial_preroll: 'widget_instream_preroll_desktop',
            },
            mobile: {
                rewarded: 'widget_instream_rewarded_mobile',
                interstitial: 'widget_instream_mobile',
                interstitial_preroll: 'widget_instream_preroll_mobile',
            },
        },
    })

    const APP_NATIVE_ANDROID_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: androidNativeAdUnits,
            desktop: {},
        },
    })

    const APP_NATIVE_ANDROID_TEST_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: androidTestNativeAdUnits,
            desktop: {},
        },
    })

    const APP_NATIVE_IOS_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: iosNativeAdUnits,
            desktop: {},
        },
    })

    const APP_NATIVE_IOS_TEST_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: iosTestNativeAdUnits,
            desktop: {},
        },
    })

    const GOOGLE_AD_SENSE_SETTINGS: AdSenseProviderSettings = createAdSenseSettings({
        adUnitConfig: {
            desktop: STATIC_GOOGLE_AD_SENSE_UNITS,
            mobile: STATIC_GOOGLE_AD_SENSE_UNITS,
        },
        targeting: {
            exp: '5883774659', // = channel group_control
        },
    })

    const HEADER_BIDDING_AD_FALLBACK_PROVIDER_SETTINGS = createHeaderBiddingAdSettings([
        TAPPX_VIDEO,
        MEDIA_NET_MULTIFORMAT,
        ONETAG,
        STARTIO,
        ADITUDE_VIDEO,
        OMNIDEX_VIDEO,
        LOOPME,
        INMOBI_VIDEO,
        EQUATIV,
        INDEX_EXCHANGE,
        PLAYGAMA_FALLBACK,
    ])

    const HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS = createHeaderBiddingAdSettings([
        TAPPX_BANNER,
        MEDIA_NET_BANNER,
        ONETAG,
        STARTIO,
        ADITUDE_DISPLAY,
        OMNIDEX_BANNER,
        LOOPME,
        INMOBI_BANNER,
        EQUATIV,
        INDEX_EXCHANGE,
        PLAYGAMA_FALLBACK,
    ])

    const HEADER_BIDDING_AD_NATIVE_APP_FALLBACK_PROVIDER_SETTINGS = createHeaderBiddingAdSettings([PLAYGAMA_FALLBACK])

    const GAME_DISTRIBUTION_PROVIDER_SETTINGS: GameDistributionsSettings = {
        type: AdProviderTypes.GAME_DISTRIBUTION,
        options: { timeout: DEFAULT_AD_UNIT_TIMEOUT, gameId: gameDistributionGameId },
    }

    const AD_PROVIDER_SETTINGS_ON_PORTAL = [
        GOOGLE_AD_PORTAL_SETTINGS,
        HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS,
    ]

    const AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE = [GOOGLE_AD_PORTAL_SETTINGS]

    const excludeInstreamOnSlowConnection = (providers: AdProvidersSettings[]) =>
        isSlowConnection ? providers.filter((p) => p.type !== AdProviderTypes.GAM_INSTREAM) : providers

    let AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE = excludeInstreamOnSlowConnection([
        GOOGLE_AD_GAME_PAGE_H5_SETTINGS,
        GOOGLE_INSTREAM_GAME_PAGE_SETTINGS,
        GOOGLE_AD_GAME_PAGE_H5_INTER_SETTINGS,
        GOOGLE_BANNER_GAME_PAGE_SETTINGS,
    ])

    let AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_INTERSTITIAL = excludeInstreamOnSlowConnection([
        GOOGLE_AD_GAME_PAGE_H5_SETTINGS,
        GOOGLE_INSTREAM_GAME_PAGE_SETTINGS,
        GOOGLE_BANNER_GAME_PAGE_SETTINGS,
    ])

    if (isVerySlowConnection) {
        AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE = []
        AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_INTERSTITIAL = []
    }

    const AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_ON_WIDGET = [
        GOOGLE_AD_WIDGET_SETTINGS,
        GOOGLE_INSTREAM_WIDGET_SETTINGS,
        GOOGLE_BANNER_GAME_PAGE_SETTINGS,
    ]

    const APP_NATIVE_ANDROID_PROVIDERS_SETTINGS = [APP_NATIVE_ANDROID_SETTINGS]
    const APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS = [APP_NATIVE_ANDROID_TEST_SETTINGS]
    const APP_NATIVE_IOS_PROVIDERS_SETTINGS = [APP_NATIVE_IOS_SETTINGS]
    const APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS = [APP_NATIVE_IOS_TEST_SETTINGS]

    const FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL = [HEADER_BIDDING_AD_FALLBACK_PROVIDER_SETTINGS]

    const FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE = [HEADER_BIDDING_AD_NATIVE_APP_FALLBACK_PROVIDER_SETTINGS]

    const FALLBACK_AD_PROVIDER_SETTINGS_PARTNER = [createHeaderBiddingAdSettings([PLAYGAMA_FALLBACK])]

    const MINIPLAY_PROVIDERS_SETTINGS = [GOOGLE_INSTREAM_GAME_PAGE_SETTINGS, GOOGLE_INSTREAM_CHEAP_MINIPLAY_SETTINGS]

    return {
        // Portal
        AD_PROVIDER_SETTINGS_ON_PORTAL,
        AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE,
        AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_INTERSTITIAL,
        AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_ON_WIDGET,

        // Portal Native
        AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE,

        // Single providers
        GAME_DISTRIBUTION_PROVIDER_SETTINGS,

        // App native
        APP_NATIVE_ANDROID_PROVIDERS_SETTINGS,
        APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS,
        APP_NATIVE_IOS_PROVIDERS_SETTINGS,
        APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS,

        // Portal fallback
        FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL,
        // Portal fallback native
        FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE,

        // Partner fallback
        FALLBACK_AD_PROVIDER_SETTINGS_PARTNER,

        // Partner-specific
        MINIPLAY_PROVIDERS_SETTINGS,

        // AdSense
        GOOGLE_AD_SENSE_SETTINGS,
    }
}
